import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TargetsOverviewTab } from "@/components/targets/TargetsOverviewTab";
import { BudgetAllocationTab } from "@/components/targets/BudgetAllocationTab";
import { FunnelEntryTab } from "@/components/targets/FunnelEntryTab";
import { Target as TargetIcon, IndianRupee, CalendarRange, Activity } from "lucide-react";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

/** Year / Quarter / Month period picker → emits a backend period string. */
function PeriodPicker({ gran, setGran, quarter, setQuarter, month, setMonth, months, monthOnly = false }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="targets-period-picker">
      {!monthOnly && (
        <ToggleGroup type="single" value={gran} onValueChange={(v) => v && setGran(v)}
          className="rounded-lg border bg-[hsl(var(--surface-2))] p-0.5">
          <ToggleGroupItem value="Year" className="h-7 px-3 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="period-year">Year</ToggleGroupItem>
          <ToggleGroupItem value="Quarter" className="h-7 px-3 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="period-quarter">Quarter</ToggleGroupItem>
          <ToggleGroupItem value="Month" className="h-7 px-3 text-xs data-[state=on]:bg-card data-[state=on]:shadow-sm" data-testid="period-month">Month</ToggleGroupItem>
        </ToggleGroup>
      )}
      {monthOnly && <span className="text-xs font-medium text-muted-foreground" data-testid="period-month-label"><CalendarRange className="mr-1 inline h-3.5 w-3.5" />Month</span>}
      {!monthOnly && gran === "Quarter" && (
        <Select value={quarter} onValueChange={setQuarter}>
          <SelectTrigger className="h-9 w-[110px]" data-testid="period-quarter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {(monthOnly || gran === "Month") && (
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-9 w-[130px]" data-testid="period-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{(months || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
}

export default function TargetsModule() {
  const { isManager } = useAuth();
  const [lists, setLists] = useState(null);
  const [tab, setTab] = useState("targets");

  // Vertical leads track targets strictly month-by-month; full-access users
  // keep the Year / Quarter / Month picker.
  const [gran, setGran] = useState(isManager ? "Month" : "Year");
  const [quarter, setQuarter] = useState("Q1");
  const [month, setMonth] = useState("Apr-26");

  useEffect(() => {
    api.lists().then((l) => {
      setLists(l);
      const ms = l.months || [];
      if (ms.length) setMonth((m) => (ms.includes(m) ? m : ms[0]));
    });
  }, []);

  // Vertical leads only see a read-only, vertical-scoped Targets view — the
  // Funnel Entry and Budget vs Allocation planning tabs are hidden from them.
  const showPlanningTabs = !isManager;
  useEffect(() => {
    if (!showPlanningTabs && tab !== "targets") setTab("targets");
  }, [showPlanningTabs, tab]);

  const period = isManager ? month : (gran === "Year" ? "FY" : gran === "Quarter" ? quarter : month);
  const periodLabel = isManager ? month : (gran === "Year" ? "FY" : gran === "Quarter" ? quarter : month);

  if (!lists) return <Loading rows={8} />;

  return (
    <div className="space-y-5" data-testid="targets-module">
      <PageHeader
        title="Targets"
        description={isManager
          ? "Your monthly targets vs achieved — Leads, Site Visits & Bookings — scoped to your assigned vertical(s)."
          : "A single, project-first view of Leads, Site Visits & Bookings — target vs achieved — with a clear Management-vs-Marketing budget picture."}
      >
        <div className="flex flex-wrap items-center gap-2">
          <PeriodPicker gran={gran} setGran={setGran} quarter={quarter} setQuarter={setQuarter}
            month={month} setMonth={setMonth} months={lists.months} monthOnly={isManager} />
        </div>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="targets-tabs">
          <TabsTrigger value="targets" data-testid="targets-tab-overview"><TargetIcon className="mr-1.5 h-3.5 w-3.5" /> Targets</TabsTrigger>
          {showPlanningTabs && (
            <TabsTrigger value="funnel" data-testid="targets-tab-funnel"><Activity className="mr-1.5 h-3.5 w-3.5" /> Funnel Entry</TabsTrigger>
          )}
          {showPlanningTabs && (
            <TabsTrigger value="budget" data-testid="targets-tab-budget"><IndianRupee className="mr-1.5 h-3.5 w-3.5" /> Budget vs Allocation</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="targets" className="mt-4">
          <TargetsOverviewTab period={period} category={null} team={null} periodLabel={periodLabel} />
        </TabsContent>

        {showPlanningTabs && (
          <TabsContent value="funnel" className="mt-4">
            <FunnelEntryTab />
          </TabsContent>
        )}

        {showPlanningTabs && (
          <TabsContent value="budget" className="mt-4">
            <BudgetAllocationTab period={period} category={null} periodLabel={periodLabel} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
